import { styled, alpha, CSSObject } from '@mui/material/styles';

import { IChipProps } from '..';

import { Chip as MuiChip } from '@mui/material';

type ExtendedChipProps = IChipProps;

const variantDefaultMixin = ({ theme, variant }: any): CSSObject => ({
  ...(variant === `outlined` && {
    borderWidth: `1px`,
    borderStyle: ` solid`,
    borderColor: theme.palette.secondary.main,
    backgroundColor: theme.palette.common.white,
    '&.MuiChip-clickable': {
      '&:hover': {
        backgroundColor: alpha(theme.palette.secondary.light, 0.08)
      },
      '&:active': {
        backgroundColor: alpha(theme.palette.secondary.light, 0.12),
        boxShadow: `none`
      }
    }
  }),
  ...(variant === `filled` && {
    color: theme.palette.text.default,
    border: `none`,
    backgroundColor: alpha(theme.palette.grayscaleSecondary.A100, 0.4),
    '.MuiChip-deleteIcon': {
      margin: 0,
      color: theme.palette.grayscaleSecondary.light
    },
    '&:hover': {
      backgroundColor: alpha(theme.palette.grayscaleSecondary.A100, 0.6)
    },
    '&:active': {
      backgroundColor: alpha(theme.palette.grayscaleSecondary.A100, 0.8)
    },
    '&.Mui-disabled': {
      opacity: 1,
      color: theme.palette.secondary[400],
      backgroundColor: alpha(theme.palette.secondary.light, 0.08),

      '.MuiChip-deleteIcon': {
        margin: 0,
        opacity: 0.24
      }
    }
  })
});

const variantMixin = ({ theme, variant, color }: any): CSSObject => ({
  ...(variant === `outlined` && {
    color: theme.palette[color].main,
    borderColor: theme.palette[color].main,
    ...(color === 'default' && {
      color: theme.palette.text.main,
      '&.MuiChip-clickable': {
        '&:hover': {
          backgroundColor: alpha(theme.palette.secondary.light, 0.08)
        },
        '&:active': {
          backgroundColor: alpha(theme.palette.secondary.light, 0.12),
          boxShadow: `none`
        }
      }
    })
  }),
  ...(variant === `filled` && {
    color: theme.palette[color].main,
    backgroundColor: alpha(theme.palette[color].main, 0.4),
    '&.MuiChip-clickable': {
      '&:hover': {
        backgroundColor: alpha(theme.palette[color].main, 0.6)
      },
      '&:active': {
        backgroundColor: alpha(theme.palette[color].main, 0.8)
      },
      '&.Mui-disabled': {
        opacity: 1,
        color: theme.palette.secondary[400],
        backgroundColor: alpha(theme.palette.secondary.main, 0.08),

        '.MuiChip-deleteIcon': {
          margin: 0,
          opacity: 0.24
        }
      }
    },
    '.MuiChip-deleteIcon': {
      margin: 0,
      color: theme.palette.grayscaleSecondary.light,
      '&:hover': {
        color: theme.palette.grayscaleSecondary.light
      }
    },
    ...(color === 'default' && {
      color: theme.palette.text.default,
      border: `none`,
      backgroundColor: alpha(theme.palette.grayscaleSecondary.A100, 0.4),
      '.MuiChip-deleteIcon': {
        margin: 0,
        color: theme.palette.grayscaleSecondary.light
      },
      '&:hover': {
        backgroundColor: alpha(theme.palette.grayscaleSecondary.A100, 0.6)
      },
      '&:active': {
        backgroundColor: alpha(theme.palette.grayscaleSecondary.A100, 0.8)
      },
      '&.Mui-disabled': {
        opacity: 1,
        color: theme.palette.secondary[400],
        backgroundColor: alpha(theme.palette.secondary.light, 0.08),

        '.MuiChip-deleteIcon': {
          margin: 0,
          opacity: 0.24
        }
      }
    })
  })
});

const sizeMixin = (size: string): CSSObject => ({
  ...(size === `small` && {
    height: `24px`,
    fontSize: `12px`,
    paddingRight: `8px`
  }),
  ...(size === `medium` && {
    height: `32px`,
    fontSize: `14px`,
    '.MuiChip-label': {
      paddingLeft: `16px`,
      '&.MuiChip-label': {
        paddingRight: `0`
      }
    },
    paddingRight: `12px`
  }),
  ...(size === `large` && {
    height: `40px`,
    fontSize: `14px`,
    '.MuiChip-label': {
      paddingLeft: `16px`,
      '&.MuiChip-label': {
        paddingRight: `0`
      }
    },
    paddingRight: `12px`
  })
});

/* styled */
export const StyledChip = styled(MuiChip, {
  shouldForwardProp: (prop) => !['type', 'readOnly', 'radius', 'isOpen'].includes(String(prop))
})<ExtendedChipProps>(
  ({ theme, variant, color, size, type, label, radius, disabled, readOnly }) => ({
    '.MuiChip-label': {
      paddingRight: `12px`
    },
    '.MuiChip-deleteIcon': {
      margin: 0,
      color: theme.palette.grayscaleSecondary.light
    },
    '.MuiChip-avatar': {
      color: theme.palette.secondary[500]
    },
    ...(radius && {
      borderRadius: `${radius}px`
    }),
    ...(size !== undefined && sizeMixin(size)),
    ...(color !== undefined &&
      color === `default` &&
      variant !== undefined &&
      type !== 'search' &&
      variantDefaultMixin({ theme, variant, color })),
    ...(color !== undefined &&
      variant !== undefined &&
      type !== 'search' &&
      variantMixin({
        theme,
        variant,
        color,
        type
      })),

    ...(type === 'search' && searchMixin({ theme, color, label, disabled, readOnly, radius }))
  })
);

const searchMixin = ({ theme, color, label, disabled, readOnly, radius }: any): CSSObject => ({
  borderRadius: `20px`,
  '.iconWrap': {
    marginLeft: `16px`
  },
  '.MuiTypography-root + .iconWrap': {
    marginLeft: 0
  },
  '.MuiChip-deleteIcon': {
    margin: 0,
    color: theme.palette.grayscaleSecondary.light,
    '&:hover': {
      color: theme.palette.grayscaleSecondary.light
    }
  },
  ...(radius && {
    borderRadius: `${radius}px`
  }),
  ...(color === 'default' && {
    color: theme.palette.text.primary,
    border: `none`,
    backgroundColor: `transparent`,

    '&:hover, &:active, &.active': {
      backgroundColor: alpha(theme.palette.secondary.main, 0.08)
    }
  }),
  ...(color === 'grayscaleSecondary' && {
    color: theme.palette.text.primary,
    border: `none`,
    backgroundColor: alpha(theme.palette.grayscaleSecondary[50], 0.4),

    '&:hover': {
      backgroundColor: alpha(theme.palette.grayscaleSecondary.A100, 0.4),
      '.iconWrap': {
        backgroundColor: alpha(theme.palette.secondary.main, 0.08)
      }
    },
    '&:active, &.active': {
      backgroundColor: alpha(theme.palette.grayscaleSecondary.A100, 0.8),
      '.iconWrap': {
        backgroundColor: alpha(theme.palette.secondary.main, 0.08)
      }
    }
  }),
  ...(color === 'primary' && {
    color: theme.palette.text.primary,
    border: `none`,
    backgroundColor: alpha(theme.palette.grayscaleSecondary.A100, 0.4),

    '&:hover, &:active, &.active': {
      backgroundColor: alpha(theme.palette.grayscaleSecondary.A100, 0.8),
      '.iconWrap': {
        backgroundColor: alpha(theme.palette.secondary.main, 0.08)
      }
    }
  }),
  ...(disabled && {
    color: theme.palette.secondary[400],
    backgroundColor: alpha(theme.palette.secondary.main, 0.08),
    '.MuiTypography-root ': {
      color: theme.palette.secondary[400]
    }
  }),
  ...(readOnly && {
    '.MuiChip-deleteIcon': {
      margin: 0,
      opacity: 0.24
    }
  })
});
